import { motion } from "framer-motion";
import {
  ShieldCheck,
  CircleDollarSign,
  Hourglass,
  TrendingDown,
  CreditCard,
  ArrowRight,
} from "lucide-react";

const features = [
  { icon: ShieldCheck, label: "Non-Recourse\nStructure" },
  { icon: CircleDollarSign, label: "Fixed 6%\nInterest" },
  { icon: Hourglass, label: "2–4 Year Terms" },
  { icon: TrendingDown, label: "Gradual Burn\nDown" },
  { icon: CreditCard, label: "Processing\nBased Model" },
];

export default function ProcessingCapital({ onApply }) {
  return (
    <section
      id="processing-capital"
      data-testid="processing-capital-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black tracking-[0.15em] md:tracking-[0.2em] text-[#0b1121] uppercase">
            Processing Based Capital
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-500 max-w-3xl mx-auto leading-relaxed">
            A smarter alternative to traditional merchant cash advances
            designed for growth-focused businesses.
          </p>
        </motion.div>

        {/* Feature row */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-y-12 gap-x-6 items-start">
          {features.map((f, idx) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
              data-testid={`processing-feature-${idx}`}
              className="flex flex-col items-center text-center group"
            >
              <div className="w-20 h-20 md:w-24 md:h-24 grid place-items-center transition-transform group-hover:-translate-y-1">
                <IconArt idx={idx} Icon={f.icon} />
              </div>
              <p className="mt-5 font-display font-bold text-lg md:text-xl text-[#0b1121] whitespace-pre-line leading-tight">
                {f.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 flex justify-center">
          <button
            data-testid="processing-capital-cta"
            onClick={() => onApply?.({ funding_purpose: "Processing Based Capital" })}
            className="group inline-flex items-center gap-3 rounded-full bg-[#0b1121] hover:bg-[#1e1b4b] text-white font-bold px-10 py-4 text-sm md:text-base tracking-[0.15em] uppercase shadow-xl hover:shadow-purple-500/40 hover:-translate-y-0.5 transition-all"
          >
            Get Your Funding Options
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * IconArt — styled circular icon container with navy + soft-purple accents
 * matching the reference (filled navy + subtle purple highlight).
 */
function IconArt({ idx, Icon }) {
  // alternate palette so the row has rhythm — navy filled vs purple filled
  const isFilled = idx === 1 || idx === 4;
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* soft purple background swatch */}
      <span
        className="absolute inset-2 rounded-full opacity-70"
        style={{
          background: isFilled
            ? "radial-gradient(circle at 30% 30%, #c4b5fd 0%, #a78bfa 100%)"
            : "radial-gradient(circle at 30% 30%, #ede9fe 0%, #ddd6fe 100%)",
        }}
      />
      <Icon
        className={`relative w-11 h-11 md:w-12 md:h-12 ${
          isFilled ? "text-[#0b1121]" : "text-[#0b1121]"
        }`}
        strokeWidth={2}
      />
    </div>
  );
}
